import { clamp, dayBounds, durationOf, snap, type GeometryOptions, type Span } from './span';

/**
 * The two edges of a block that can be resized. The values are also the `data-edge` attribute
 * values of the resize handles (data dictionary section 2).
 */
export const ResizeEdge = { Start: 'start', End: 'end' } as const;
export type ResizeEdge = (typeof ResizeEdge)[keyof typeof ResizeEdge];

/** One step of a flipping resize: the new span and the side of the anchor the handle is on. */
export interface ResizeResult<T extends Span> {
  readonly span: T;
  /** `End` when the span lies after the anchor (the handle is the end edge), `Start` before it. */
  readonly side: ResizeEdge;
}

/**
 * Length floor of a resize (2026-10b §3.3): the minimum duration, but a block already shorter
 * keeps its own length as the floor — dragging inward never silently grows it over a neighbour.
 */
function resizeFloor(block: Span, options: GeometryOptions): number {
  return Math.min(Math.max(durationOf(block), 1), options.minDuration);
}

/**
 * One step of an edge drag with flipping (redesign §2.2, Outlook behaviour). `anchorMin` is the
 * opposite edge of the dragged one, recorded at press and fixed for the whole drag; `pointerMin`
 * is the dragged position; `side` is the side of the anchor the handle was on after the previous
 * step (initially the dragged edge itself). The pointer snaps to the step — unless
 * `pointerExact` says a neighbour detent hit (2026-10b §3.6), which keeps the neighbour's exact
 * boundary — and is held inside the personal day; the span is [min(anchor, p), max(anchor, p)].
 * A span shorter than the floor (the minimum duration, or the block's own length when shorter)
 * extends toward the pointer — toward `side` when p equals the anchor — so the handle flips to
 * the other edge when it crosses the anchor. The user dragging an edge into another block still
 * overlaps it (blocks are no obstacle), but the floor compensation never pushes the edge into a
 * neighbour: it stops at the nearest boundary in `others` (cutting wins, §3.3); the personal
 * day is the only other hard limit.
 */
export function resizeBlock<T extends Span>(
  block: T,
  anchorMin: number,
  pointerMin: number,
  options: GeometryOptions,
  side: ResizeEdge,
  others: readonly Span[] = [],
  pointerExact = false,
): ResizeResult<T> {
  const bounds = dayBounds(options.dayStartMin);
  const snapped = pointerExact ? pointerMin : snap(pointerMin, options.step);
  const pointer = clamp(snapped, bounds.startMin, bounds.endMin);
  const nextSide = pointer > anchorMin ? ResizeEdge.End : pointer < anchorMin ? ResizeEdge.Start : side;
  let startMin = Math.min(anchorMin, pointer);
  let endMin = Math.max(anchorMin, pointer);
  const floor = resizeFloor(block, options);
  if (endMin - startMin < floor) {
    if (nextSide === ResizeEdge.End) {
      const limits = others.map((other) => other.startMin).filter((min) => min >= endMin && min > anchorMin);
      endMin = Math.max(endMin, Math.min(anchorMin + floor, bounds.endMin, ...limits));
    } else {
      const limits = others.map((other) => other.endMin).filter((min) => min <= startMin && min < anchorMin);
      startMin = Math.min(startMin, Math.max(anchorMin - floor, bounds.startMin, ...limits));
    }
  }
  return { span: { ...block, startMin, endMin }, side: nextSide };
}

/**
 * Non-flipping resize for keyboard nudges (2026-10b §3.5): the chosen edge moves to `toMin`
 * (snapped to the step) and stops the floor (minimum duration, or the block's own shorter
 * length) short of the opposite edge and at the day bounds. Other blocks are no obstacle. The
 * opposite edge never moves. A block already shorter than its floor at a day bound has no
 * valid position, so the edge stays at the day bound instead of leaving the personal day.
 */
export function resizeBlockNoFlip<T extends Span>(
  block: T,
  edge: ResizeEdge,
  toMin: number,
  options: GeometryOptions,
): T {
  const bounds = dayBounds(options.dayStartMin);
  const target = snap(toMin, options.step);
  const floor = resizeFloor(block, options);
  if (edge === ResizeEdge.Start) {
    const high = Math.max(bounds.startMin, block.endMin - floor);
    return { ...block, startMin: clamp(target, bounds.startMin, high) };
  }
  const low = Math.min(bounds.endMin, block.startMin + floor);
  return { ...block, endMin: clamp(target, low, bounds.endMin) };
}

/**
 * Move `block` so that it starts at `newStartMin` (snapped to the step), keeping its duration
 * (redesign §2.4). Other blocks are no obstacle (overlap is allowed); the personal day is the
 * only hard limit, so the block follows the pointer step by step with no gap jumping.
 */
export function moveBlock<T extends Span>(block: T, newStartMin: number, options: GeometryOptions): T {
  const bounds = dayBounds(options.dayStartMin);
  const duration = durationOf(block);
  const startMin = clamp(snap(newStartMin, options.step), bounds.startMin, bounds.endMin - duration);
  return { ...block, startMin, endMin: startMin + duration };
}
